import { reactive, ref, toRaw } from 'vue';
import ApiManager from '../api/index';
import { REGION } from '../constants/index';
import { ZH_CN_LANGUAGE, EN_US_LANGUAGE } from '../../assets/language/index';
import { OnlineGameConfig, LoginConfig } from '../../types/api';

/**
 * 全局配置管理器
 */

interface GlobalConfig {
  gameId: string;
  language?: string;
  region?: string;
  environment?: string;
  packageName?: string;
  isNeedWechatPay?: boolean; // 是否需要微信内h5支付
  // autoDownConfig?: {
  //   account?: boolean;
  //   sms?: boolean;
  // };
  [key: string]: any;
}

/**
 * 全局配置实例
 */
class ConfigManager {
  private static instance: ConfigManager | null = null;
  private config = reactive<GlobalConfig>({} as GlobalConfig);
  private gameConfig = reactive<OnlineGameConfig>({} as OnlineGameConfig);
  private language = reactive<{ [key: string]: string }>({});
  private isInitialized = ref(false);

  public static id: string;

  /**
   * 获取单例实例
   */
  static getInstance(): ConfigManager {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager();
    }
    ConfigManager.id = (new Date()).toString();
    return ConfigManager.instance;
  }

  /**
   * 私有构造函数，确保单例模式
   */
  private constructor() {}

  /**
   * 初始化配置
   * @param config 配置对象
   */
  async init(config: GlobalConfig): Promise<void> {
    
    try {
      // 加载配置项
      const response = await ApiManager.getInstance().game.getConfig();
      if (response.code !== 200) {
        return Promise.reject(response);
      }

      const ua = window.navigator.userAgent.toLowerCase()
      // 如果在手机微信内且需要支付的话需要先微信授权
      if (config.isNeedWechatPay && ua.includes('micromessenger') && !ua.includes('windowswechat')) {
        const openId = sessionStorage.getItem('wechatOpenId')
        if(!openId) {
          const wechatResponse = await ApiManager.getInstance().pay.getWechatOpenId()
          if (wechatResponse.code !== 200) {
            return Promise.reject(wechatResponse)
          }
          if (wechatResponse.data.open_id) {
            sessionStorage.setItem('wechatOpenId', wechatResponse.data.open_id);
          } else {
            window.location.href = "https://api.qingcigame.com/wechat/get/open/?back=" + encodeURIComponent(window.location.href)
          }
        }
      }
      const gameConfig = response.metadata;
      
      // 更新响应式数据
      Object.assign(this.gameConfig, gameConfig);
      Object.assign(this.config, config);
      
      let languageData = config.region === REGION.DOMESTIC ? ZH_CN_LANGUAGE : EN_US_LANGUAGE ;
      const languageStorage = JSON.parse(localStorage.getItem('languageMap') || '{}');
      const { url, version, language } = gameConfig.language;
      if (url) {
        if (languageStorage[language] && languageStorage[language].version === version) {
          languageData = {...languageData, ...languageStorage[language].data};
        } else {
          const languageResponse = await ApiManager.getInstance().game.fetchLanguage(url);
          languageData = {...languageData, ...languageResponse };
          languageStorage[language] = { version, data: languageResponse };
          localStorage.setItem('languageMap', JSON.stringify(languageStorage));
        }
      }
      
      // 清空并重新赋值语言数据，确保响应式更新
      Object.keys(this.language).forEach(key => {
        delete this.language[key];
      });
      Object.assign(this.language, languageData);
      
      this.isInitialized.value = true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * 获取配置
   * @param key 配置键名
   * @returns 配置值
   */
  get<T = any>(key: string): T {
    if (!this.isInitialized.value) {
      throw new Error('配置未初始化，请先调用 init() 方法');
    }
    return this.config[key] as T;
  }
  /**
   * 获取所有配置
   * @returns 完整配置对象
   */
  getAll(): GlobalConfig {
    if (!this.isInitialized.value) {
      throw new Error('配置未初始化，请先调用 init() 方法');
    }
    return toRaw(this.config);
  }

  getLoginWays(): LoginConfig {
    if (!this.isInitialized.value) {
      throw new Error('配置未初始化，请先调用 init() 方法');
    }
    return this.gameConfig.login;
  }

  getCustomer() {
    if (!this.isInitialized.value) {
      throw new Error('配置未初始化，请先调用 init() 方法');
    }
    return this.gameConfig.customer;
  }

  getLanguage() {
    if (!this.isInitialized.value) {
      throw new Error('配置未初始化，请先调用 init() 方法');
    }
    return this.language;
  }

  getCompany() {
    if (!this.isInitialized.value) {
      throw new Error('配置未初始化，请先调用 init() 方法');
    }
    return this.gameConfig.language.company;
  }

  /**
   * 更新配置
   * @param config 部分配置对象
   */
  async update(config: Partial<GlobalConfig>): Promise<void> {
    if (!this.isInitialized.value) {
      throw new Error('配置未初始化，请先调用 init() 方法');
    }
    await this.init({
      ...toRaw(this.config),
      ...config
    })
  }
}

// 创建全局配置实例
const configManager = ConfigManager.getInstance();

export default configManager;
export type { GlobalConfig }; 